import { Injectable, NgZone } from "@angular/core";
import { Editor } from '@tiptap/core';
import { GlobalService } from "../global.service";
import { AppInjector } from "app/app.module";
import Extentions from './extensions';
import { translate } from "@jsverse/transloco";
import { EditorEmitterService } from "app/main/pages/events/editor-emitter.service";
import { debounceTime, Subject, Subscription, takeUntil } from "rxjs";
import { Block } from "app/interfaces/blocks";
import { Placeholder } from '@tiptap/extensions'

@Injectable({
    providedIn: 'root'
})
// Initialize a variable to hold the Tippy instance
export class TipTapEditorService {
    constructor(private ngZone: NgZone, private editorEventEmitter:EditorEmitterService) { }
    private instance: Editor;
    private isEditorFocused: boolean = false;
    private contentUpdate = new Subject<Editor>();
    private destroyed = new Subject<void>();
    private subscriptions = new Subscription();
    private timeoutId?: ReturnType<typeof setTimeout>;
    initEditor(documentElement: HTMLElement, uuid, clickCoords?:{left:number,top:number}) {
        const globalService = AppInjector.get(GlobalService);

        // safe lookup for blocks container and the specific block
        const blocksContainer = globalService?.fields_data?.['blocks'] ?? null;
        let block: Block | undefined = blocksContainer ? blocksContainer[uuid] : undefined;

        this.subscriptions = this.contentUpdate.pipe(debounceTime(300), takeUntil(this.destroyed)).subscribe(() => {
            this.ngZone.run(() => {
                globalService.backSpaceScript();
            });
        });
        let documentContent:string = documentElement.innerHTML.replace(/<br\s+class="ProseMirror-trailingBreak"\s*\/?>/g, '');
        if(block?.type === 'content-box'){
            documentContent = documentContent.replace(/ {2,}/g, match => '&nbsp;'.repeat(match.length));
        }
        documentElement.innerHTML = '';
        documentElement.classList.add('cke-editor-placeholder');
        documentElement.classList.add('cke-editor-box');

        globalService.editorInstances[uuid] = new Editor({
            editable: block?.is_locked === 1 ? false : true,
            onFocus : ({editor}) => {
                const editorToolbar = document.querySelector('.editor-toolbar');
                if(editorToolbar){
                    clearTimeout(this.timeoutId);
                    this.timeoutId = null;
                    return;
                }
                if (this.timeoutId) {
                    clearTimeout(this.timeoutId); // Clear any pending blur timeout
                    this.timeoutId = null;
                }
                this.handleFocus(editor);
                this.isEditorFocused = true;
            },
            onBlur : ({event, editor}) => {
                const editorToolbar = (event?.relatedTarget as HTMLInputElement)?.classList.contains('editor-toolbar');
                if((event.relatedTarget as HTMLInputElement)?.nodeName === "INPUT" || (event.relatedTarget as HTMLElement)?.classList.contains('toolbar-color-picker')){
                    clearTimeout(this.timeoutId);
                    this.timeoutId = null;
                    return;
                }else if(editorToolbar){
                    clearTimeout(this.timeoutId);
                    this.timeoutId = null;
                    editor.commands.focus();
                    return;
                }

                this.timeoutId = setTimeout(() => {
                    this.editorEventEmitter.updateEditor.next(null);
                    this.isEditorFocused = false;   
                }, 500);
            },
            onUpdate: ({editor}) => {
                this.contentUpdate.next(editor);
            },
            onTransaction: () => {
                return false;
            },
            editorProps: { 
                attributes: { uuid: uuid, spellcheck: 'false', class: 'outline-0' },
                handleClick: () => {
                    // re-resolve safely in case fields_data changed after init
                    const blocks = globalService?.fields_data?.['blocks'] ?? null;
                    block = blocks ? blocks[uuid] : undefined;

                    // if block missing or locked, do nothing
                    if (!block) {
                        console.warn('[TipTapEditorService] handleClick: block data missing for uuid', uuid);
                        return;
                    }
                    if (block?.is_locked === 1) {
                        return;
                    }

                    // guard editorInstances as well
                    const editorInstance = globalService?.editorInstances?.[uuid];
                    if (!editorInstance) {
                        console.warn('[TipTapEditorService] handleClick: editor instance not found for uuid', uuid);
                        return;
                    }

                    this.handleFocus(editorInstance);
                },
            },
            extensions: [...Extentions,...[Placeholder.configure({placeholder: translate("Content block html text on content-box (Template/Document - section) - Type / for quick actions")})]],
            content: documentContent,
        });

        // Append editor dom only if instance created
        const editor = globalService?.editorInstances?.[uuid];
        if (!editor) {
            console.warn('[TipTapEditorService] initEditor: failed to create editor instance for uuid', uuid);
            return;
        }

        documentElement.appendChild(editor.view.dom);

        if(block?.is_locked !== 1){
            if(clickCoords){
                const pos = editor?.view?.posAtCoords(clickCoords);
                if (pos) {
                    editor.commands.setTextSelection({ from: pos.pos, to: pos.pos });
                    editor.view.focus();
                }
            }else{
                editor.commands.focus();
            }
        }
    }

    private handleFocus(editor: Editor) {
        this.editorEventEmitter.updateEditor.next(editor);
    }

    destroy(uuid:number, globalService:GlobalService){
        if(this.subscriptions){
            this.subscriptions.unsubscribe();
        }
        this.destroyed.next();
        this.destroyed.complete();
        globalService.editorInstances[uuid]?.destroy();
        delete globalService.editorInstances[uuid];
    }
}